import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { $t } from "~/i18n.js";

import { PrinterOutlined } from "@ant-design/icons";
import { Button, Input, Select, Table } from "antd";

import { MainLayout } from "~/components/layout/layout.jsx";
import { UserModal } from "~/components/profile/userModal.jsx";
import { api } from "~/lib/api.js";
import { daysActions, flightsActions } from "~/redux/actions.js";
import {
  DTOlijst,
  clusterColumn
} from "./table.columns.jsx";

import { toLocaleDate } from "~/lib/localize.js";

import { useMedia } from "use-media";
import { RefreshBaseData } from "~/components/buttons/RefreshBaseData.jsx";
import { BreadcrumbHeader } from "~/components/layout/breadcrumbHeader.jsx";
import { defaultTableConfig } from "~/components/layout/table.jsx";
import { selectElementByID } from "~/lib/helpers.js";
import { clubHasFlight } from "~/featureMatrix.jsx";

export const DTOOverzicht = ({ history, match }) => {
  
  // TODO: badge debug
  const hasBadges = localStorage.getItem("badges") === "true";

  const [baseVliegers, setBaseVliegers] = useState([]);
  const [DTOvliegers, setDTOvliegers] = useState({});
  const [vliegers, setVliegers] = useState([]);
  
  const dispatch = useDispatch();
  const persist = useSelector((state) => state.persist);
  const flights = useSelector((state) => state.flights);
  const days = useSelector((state) => state.days);
  
  const getBaseData = (d) => dispatch(flightsActions.getBaseData(d));
  const getAllDays = (d) => dispatch(daysActions.getAllDays(d));
  useEffect(() => setColumns(), []);

  useEffect(() => {
    // initial, if any of the DTO vliegers or vliegers changes, then reset the list
    const groups_exclude = persist.club?.groups_exclude
      ? persist.club.groups_exclude.split(",").map((g) => g.trim()).filter(Boolean)
      : [];

    let baseVliegers = flights.vliegers.filter((item) => item.id > 0)
    if (DTOvliegers && Object.keys(DTOvliegers).length > 0) {
      baseVliegers = baseVliegers.filter((item) => DTOvliegers[item.id]);
    } else {
      baseVliegers = [];
    }

    if (groups_exclude.length > 0) {
      baseVliegers = baseVliegers.filter(
        (v) => !v.group_keys?.some((k) => groups_exclude.includes(k))
      );
    }

    setBaseVliegers(baseVliegers);
    setVliegers(baseVliegers);
  }, [flights.vliegers, DTOvliegers]);
  
  const [search, setSearch] = useState("");

  const isWide = useMedia({ minWidth: "450px" }, false);

  const searchVlieger = (searchText) => {
    const newSearch = searchText?.toLowerCase();
    let vliegers = baseVliegers
    if (newSearch) {
      vliegers = vliegers.filter(
        (vlieger) => vlieger.lowercase.indexOf(newSearch) > -1
      );
    }
    setVliegers(vliegers);
  };

  const [behaaldeBevoegdheden, setBevoegdheden] = useState({});
  const [activeDtoTrainingen, setActiveDtoTrainingen] = useState({});
  const [deactivatedLeden, SetDeactivatedLeden] = useState(null);
  const [notities, setNotities] = useState({});
  const [latestBehaaldeBevoegdheden, setLatestBehaaldeBevoegdheden] = useState({});
  const [tableColumns, setStateColumns] = useState();

  const [loadingFile, setFileLoading] = useState(false);

  // using router to navigate opened pilot
  const [openedPilot, setOpenPilot] = useState(null);
  useEffect(() => {
    setOpenPilot({
      id: match.params.id,
      ...selectElementByID(
        flights.vliegers,
        "id",
        parseInt(match.params.id || 0)
      ),
    });
  }, [match.params.id]);

  const setColumns = (
    newBevoegdheden,
    newDTOtrainingen,
    newlatestBehaaldeBevoegdheden
  ) => {
    const selectedBehaaldeBevoegdheden = newBevoegdheden || behaaldeBevoegdheden;
    const selectedActiveDtoTrainingen = newDTOtrainingen || activeDtoTrainingen;
    const groups_filter = persist.club && persist.club.groups_filter;
    const group_name_lookup = flights?.group_name_lookup || {};

    // set column to render table
    let columns = [];

    columns = DTOlijst(
      groups_filter,
      group_name_lookup,
      newlatestBehaaldeBevoegdheden || latestBehaaldeBevoegdheden,
      history,
      clubHasFlight(),
      hasBadges
    );

    // iterate over bevoegdheden columns
    flights.bevoegdheden.forEach(
      (cluster) =>
        cluster.is_dto &&
        columns.push(
          clusterColumn(
            cluster,
            selectedBehaaldeBevoegdheden,
            selectedActiveDtoTrainingen
          )
        )
    );

    // we push the columns to a state to update the table
    setStateColumns(columns);
  };

  const [DTOloading, setDTOloading] = useState(false);
  const loadingData = DTOloading || flights.isPending || days.isPending
  const getDTOdata = async () => {
    setDTOloading(true);
    const res = await api.get("instructie/dto_data.json");

    if (res) {
      setBevoegdheden(res.behaalde_bevoegdheden);
      setActiveDtoTrainingen(res.active_dto_trainingen);
      SetDeactivatedLeden(res.deactivated_leden);
      setLatestBehaaldeBevoegdheden(res.latest_behaalde_bevoegdheden || {});

      // set all started vliegers in list to filter
      const dtoVliegers = {};
      Object.keys(res.active_dto_trainingen).forEach(function (key) {
        if (res.active_dto_trainingen[key] === "started") {
          dtoVliegers[parseInt(key.split("_")[0])] = 1;
        }
      });
      setDTOvliegers(dtoVliegers);

      // set columns initially
      setColumns(
        res.behaalde_bevoegdheden,
        res.active_dto_trainingen,
        res.latest_behaalde_bevoegdheden
      );

      setDTOloading(false);
    }
  };

  // loading in all data, flights, days and dto information
  useEffect(() => {
    const loadData = async () => {
      // load in base data if needed
      if (!flights.lastUpdatedBaseData) await getBaseData();
      if (!days.lastUpdated) await getAllDays();
      getDTOdata();
    };

    // when opening
    loadData();
  }, [flights.lastUpdatedBaseData, days.lastUpdated]);

  return (
    <>
      <UserModal
        link={window.location.href}
        visible={openedPilot && openedPilot.id}
        onClose={() => history.replace("/dto")}
        pilot={openedPilot}
        newNotities={(_new) => {
          notities[openedPilot.id] = _new;
          setNotities(notities);
          setColumns();
        }}
      />

      <MainLayout history={history}>
        <BreadcrumbHeader
          breadcrumbs={[$t("DTO leerlingvolglijst")]}
          buttons={[
            <RefreshBaseData
              key="1"
              style={{ marginRight: 8 }}
              extra={getDTOdata}
              loading={loadingData}
            />,

            isWide && persist.profile.wijzig_dto && (
              <Button
                icon={<PrinterOutlined />}
                loading={loadingFile}
                size='small'
                onClick={async () => {
                  setFileLoading(true);
                  await api.open_file(
                    `bevoegdheid/dto/export.json`,
                    `${$t("bevoegheden")} ${toLocaleDate(new Date())}.xlsx`
                  );
                  setFileLoading(false);
                }}
              >
                {$t("Exporteer")}
              </Button>
            ),
          ]}
        />

        <div className="column" style={{ alignItems: "flex-start" }}>
          <div className="row" style={{ gap: 12 }}>
            <Input.Search
              style={{ maxWidth: 250 }}
              value={search}
              disabled={loadingData}
              allowClear
              size="small"
              placeholder={$t("Zoek op naam")}
              onChange={(event) => {
                searchVlieger(event.target.value);
                setSearch(event.target.value);
              }}
            />

            {search.length > 0 && (
              <Button size="small" onClick={() => {
                searchVlieger("");
                setSearch("");
              }}>
                {$t("Verwijder")}
              </Button>
            )}
          </div>

          {deactivatedLeden?.length > 0 && (
            <Select
              mode="single"
              showSearch
              disabled={loadingData}
              style={{ marginTop: 12, maxWidth: 250, width: "100%" }}
              size="small"
              onSelect={(vliegerID) => history.push(`/dto/${vliegerID}`)}
              filterOption={(input, option) =>
                option?.children?.toLowerCase().indexOf(input.toLowerCase()) >=
                0
              }
              placeholder={$t("Open deactief lid")}
            >
              {deactivatedLeden.map(
                (vlieger, key) =>
                  vlieger.id > 0 && (
                    <Select.Option value={vlieger.id} key={key}>
                      {vlieger.name}
                    </Select.Option>
                  )
              )}
            </Select>
          )}

          <Table
            loading={DTOloading || flights.isPending || days.isPending}
            style={{
              marginTop: 12,
              width: "95vw",
              overflowX: "auto",
              cursor: "pointer",
            }}
            pagination={{ ...defaultTableConfig, defaultPageSize: 100 }}
            size="small"
            rowKey="id"
            dataSource={vliegers}
            columns={tableColumns}
          />
        </div>
      </MainLayout>
    </>
  );
};
